import { Pencil, Trash2, Eye, Calendar, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import FeeHistoryTable from "./FeeHistoryTable";
import axiosInstance from "../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../constants/config";
import { getFullName } from "../../../utils/urlUtils";

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
    <span className="text-gray-600 font-medium">{label}</span>
    <span className="text-gray-800">{value || "Not provided"}</span>
  </div>
);

export default function StudentProfile({
  student,
  studentRouteName,
  activeTab,
  setActiveTab,
  studentSubjects,
  loadingSubjects,
  onEdit,
  onDelete,
  onRefresh,
  onError,
}) {
  const handleProfileImageUpload = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.put(API_ENDPOINTS.user(student._id), formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      onRefresh();
      onError("");
    } catch (error) {
      console.error("Failed to update profile image:", error);
      onError("Failed to update profile image. Please try again.");
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-4 md:p-6 rounded-t-lg">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
          <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-4">
            <div className="relative group">
              <img
                src={student.profileImage || "/student_profile.png" || "/placeholder.svg"}
                alt={getFullName(student)}
                className="w-16 h-16 md:w-20 md:h-20 rounded-full border-4 border-white"
              />
              <span className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></span>
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
                <div className="flex gap-1">
                  <button
                    type="button"
                    className="w-7 h-7 rounded-full bg-white/80 flex items-center justify-center hover:bg-white transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      const fileInput = document.createElement("input");
                      fileInput.type = "file";
                      fileInput.accept = "image/*";
                      fileInput.onchange = (event) => handleProfileImageUpload(event.target.files[0]);
                      fileInput.click();
                    }}
                  >
                    <Pencil className="w-3 h-3 text-gray-700" />
                  </button>
                  <button
                    type="button"
                    className="w-7 h-7 rounded-full bg-white/80 flex items-center justify-center hover:bg-white transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (student.profileImage) window.open(student.profileImage, "_blank");
                    }}
                  >
                    <Eye className="w-3 h-3 text-gray-700" />
                  </button>
                </div>
              </div>
            </div>
            <div className="text-white">
              <h2 className="text-xl md:text-2xl font-bold">{getFullName(student)}</h2>
              <p className="text-blue-100">@{student.username}</p>
              <p className="text-blue-100">
                Class {student.classMapping?.class?.name || "Unassigned"} -{" "}
                {student.classMapping?.section?.name || "Unassigned"}
              </p>
            </div>
          </div>
          <div className="flex space-x-2 w-full md:w-auto">
            <Button
              variant="secondary"
              className="flex-1 md:flex-none bg-white/10 hover:bg-white/20 text-white"
              onClick={onEdit}
            >
              <Pencil className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
            <Button
              variant="destructive"
              className="flex-1 md:flex-none bg-red-500/10 hover:bg-red-500/20 text-white"
              onClick={onDelete}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b overflow-x-auto">
        <div className="flex px-4 md:px-6 min-w-max">
          {["profile", "subjects", "feeHistory"].map((tab) => (
            <Button
              key={tab}
              variant="ghost"
              className={`px-4 md:px-6 py-3 -mb-px whitespace-nowrap ${
                activeTab === tab
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-500"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1).replace(/([A-Z])/g, " $1")}
            </Button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-4 md:p-6">
        {activeTab === "profile" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-800">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <InfoRow label="Roll Number" value={student.classMapping?.rollNumber || "Not Set"} />
                <InfoRow label="Admission Number" value={student.admissionNumber} />
                <InfoRow label="Aadhaar Card" value={student.aadhaarCard} />
                <InfoRow
                  label="Date of Birth"
                  value={student.birthday && new Date(student.birthday).toLocaleDateString()}
                />
                <InfoRow label="Category" value={student.category} />
                <InfoRow label="Blood Group" value={student.bloodGroup} />
                <InfoRow label="Gender" value={student.gender} />
                <InfoRow
                  label="Transportation"
                  value={(() => {
                    if (!student.transportationType) return "Not set";
                    const t = student.transportationType;
                    return `${t.charAt(0).toUpperCase() + t.slice(1)}${
                      t === "bus" ? ` (Route: ${studentRouteName || "Not assigned"})` : ""
                    }`;
                  })()}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-800">Contact Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <InfoRow label="Email" value={student.email} />
                <InfoRow label="Mobile" value={student.phoneNumber} />
                <InfoRow
                  label="Address"
                  value={
                    student.address
                      ? `${student.address.street}, ${student.address.city}, ${student.address.state} - ${student.address.pincode}`
                      : "Not provided"
                  }
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-800">Academic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <InfoRow label="Class" value={student.classMapping?.class?.name || "Unassigned"} />
                <InfoRow label="Section" value={student.classMapping?.section?.name || "Unassigned"} />
                <InfoRow label="Roll Number" value={student.classMapping?.rollNumber || "Not Set"} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-800">Guardian Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <InfoRow label="Father's Name" value={student.fatherName} />
                <InfoRow label="Father's Contact" value={student.fatherContactNumber} />
                <InfoRow label="Mother's Name" value={student.motherName} />
                <InfoRow label="Mother's Contact" value={student.motherContactNumber} />
                <InfoRow label="Guardian's Name" value={student.guardianName} />
                <InfoRow label="Guardian's Contact" value={student.guardianContactNumber} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-800">Medical Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <InfoRow label="Medical History" value={student.medicalHistory} />
                <InfoRow label="Special Notes" value={student.note} />
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "subjects" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-800">Enrolled Subjects</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingSubjects ? (
                <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading subjects...</span>
                </div>
              ) : studentSubjects.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">
                  No subjects assigned. Go to Settings → Section Subjects to assign subjects.
                </p>
              ) : (
                <div className="divide-y rounded-md border">
                  {studentSubjects.map((enrollment) => {
                    const typeLabel = { core: "Core", elective: "Elective", optional: "Optional" }[enrollment.subjectType] ?? "Elective";
                    const typeBadgeColor =
                      enrollment.subjectType === "optional"
                        ? "bg-purple-100 text-purple-700"
                        : enrollment.subjectType === "core"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-gray-100 text-gray-600";
                    const isActive = enrollment.status === "active";
                    return (
                      <div key={enrollment._id} className="flex items-center justify-between px-4 py-3">
                        <div>
                          <p className="text-sm font-medium">{enrollment.subject?.name}</p>
                          <p className="text-xs text-muted-foreground">{enrollment.subject?.code}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeBadgeColor}`}>{typeLabel}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                            {isActive ? "Active" : "Dropped"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === "feeHistory" && (
          <div className="overflow-x-auto">
            <FeeHistoryTable
              studentId={student._id}
              onSendReminder={(record) => console.log("Send reminder for:", record)}
              onSaveChanges={(record) => console.log("Save changes for:", record)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
